import type {RouteLocationRaw, RouteRecordRaw} from 'vue-router';
import {createPropositionGuard, useProposition} from '@myparcel-frontend/proposition';
import {SystemMessageType} from '@myparcel-frontend/types';
import {OrdersView, resolveOrdersView} from './orders.routes';
import useFeatures from '@/composables/useFeatures';
import {useOrderVersion} from '@/composables/useOrderVersion/useOrderVersion';

const LEGACY_SHIPMENTS_COOKIE = 'shipments-legacy=true; path=/; max-age=60; SameSite=Strict';
const CLEAR_LEGACY_SHIPMENTS_COOKIE = 'shipments-legacy=; path=/; max-age=0; SameSite=Strict';

/**
 * Mirror of the orders `resolveOrdersView` gate for the shipment-edit route: order management and
 * the vue port of v1 open the Vue page, meteor v1 gets bounced to the meteor shipment form.
 */
const resolveShipmentEditRoute = (shipmentId: string): RouteLocationRaw | boolean => {
  const view = resolveOrdersView();

  if (view === OrdersView.V1Meteor) {
    document.cookie = LEGACY_SHIPMENTS_COOKIE;
    window.location.replace(`/shipmentform?shipment=${shipmentId}`);
    return false;
  }

  document.cookie = CLEAR_LEGACY_SHIPMENTS_COOKIE;
  return true;
};

const resolveShipmentRoute = (): RouteLocationRaw | boolean => {
  const {hasOrderManagement} = useOrderVersion();

  // Only redirect to legacy meteor on a positive legacy signal. If IAM flags fail to load they
  // all default to false, but meteor's own iamStore still sees ORDER_MANAGEMENT and would redirect
  // back here, causing a redirect loop. When unsure, stay on Meteor.
  if (hasOrderManagement) {
    document.cookie = CLEAR_LEGACY_SHIPMENTS_COOKIE;
    return true;
  }

  document.cookie = LEGACY_SHIPMENTS_COOKIE;
  window.location.replace('/shipments/create');
  return false;
};

const resolveShipmentsOverviewRoute = (): RouteLocationRaw | boolean => {
  const {isFeatureActive} = useFeatures();

  if (isFeatureActive('page.shipments.overview')) {
    document.cookie = CLEAR_LEGACY_SHIPMENTS_COOKIE;
    return true;
  }

  const {proposition} = useProposition();
  if (proposition.value.shipments.hasShipmentsOverview) {
    document.cookie = LEGACY_SHIPMENTS_COOKIE;
    window.location.replace('/shipments');
    return false;
  }

  document.cookie = CLEAR_LEGACY_SHIPMENTS_COOKIE;
  return {name: 'dashboard'};
};

export const shipmentRoutes: RouteRecordRaw[] = [
  {
    path: '/shipments',
    children: [
      {
        name: 'shipments',
        path: '',
        component: () => import('@/views/Shipments/Overview/Overview.vue'),
        beforeEnter: [resolveShipmentsOverviewRoute],
        meta: {
          permissions: ['selectShipment'],
          title: (t) => t('shipment.index.shipment', 2),
        },
      },
      {
        path: 'create',
        component: () => import('@/views/Shipments/Create/CreateIndex.vue'),
        children: [
          {
            name: 'shipments-create',
            path: '',
            component: () => import('@/views/Shipments/Create/Create.vue'),
            beforeEnter: [resolveShipmentRoute],
            meta: {
              permissions: ['insertShipment'],
              title: (t) => t('shipment.newShipment'),
              systemMessageType: SystemMessageType.SHIPMENT_FORM,
            },
          },
          {
            name: 'shipments-create-import-export',
            path: 'import-export',
            component: () => import('@/views/Shipments/Create/ImportExport/ImportExport.vue'),
            beforeEnter: [
              createPropositionGuard((proposition) => proposition.shipments.enableGoodsImportExport, {
                redirect: {name: 'dashboard'},
                path: '/dashboard',
              }),
            ],
            meta: {
              permissions: ['insertShipment'],
              title: (t) => t('shipment.importExport.title'),
            },
          },
        ],
      },
      {
        name: 'shipments-import',
        path: 'import',
        component: () => import('@/views/Empty.vue'),
        beforeEnter: [
          createPropositionGuard((proposition) => proposition.shipments.enableCsvImport, {
            redirect: {name: 'dashboard'},
            path: '/dashboard',
          }),
        ],
        meta: {
          feature: 'page.shipments.import',
          permissions: ['insertShipment'],
          title: (t) => t('shipment.import'),
        },
      },
      {
        name: 'shipments.return.create',
        path: 'return/create',
        component: () => import('@/views/Empty.vue'),
        beforeEnter: [
          createPropositionGuard((proposition) => proposition.shipments.enableReturn, {
            redirect: {name: 'dashboard'},
            path: '/dashboard',
          }),
        ],
        meta: {
          feature: 'page.shipments.createReturn',
          permissions: ['insertShipment'],
          title: (t) => t('shipment.newShipment'),
          systemMessageType: SystemMessageType.SHIPMENT_FORM,
        },
      },
      {
        name: 'shipments-pallet-create',
        path: 'pallet/create',
        component: () => import('@/views/Shipments/Pallet/Create/CreatePallet.vue'),
        beforeEnter: [
          createPropositionGuard((proposition) => proposition.shipments.enablePallet, {
            redirect: {name: 'dashboard'},
            path: '/dashboard',
          }),
        ],
        meta: {
          feature: 'page.shipments.createPallet',
          permissions: ['insertShipment'],
          title: (t) => t('shipment.pallet.label'),
          systemMessageType: SystemMessageType.SHIPMENT_FORM,
        },
      },
      {
        name: 'shipment-edit',
        path: ':id',
        props: true,
        component: () => import('@/views/Orders/V1/ShipmentEdit/Edit.vue'),
        beforeEnter: (to) => resolveShipmentEditRoute(String(to.params.id)),
        meta: {
          permissions: ['updateShipment'],
          title: (t) => t('shipments.editShipment.title'),
        },
      },
    ],
  },
];
